import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Dialog as UIDialog,
} from '../ui/dialog'
import { VisuallyHidden } from '../ui/visually-hidden'
import type { ReactNode } from 'react'
import { useIsMobile } from '@/hooks'

interface DialogProps extends React.ComponentProps<typeof UIDialog> {
  open?: boolean
  onClose?: () => void
  title?: string
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  trigger: ReactNode
  contentClassName?: string
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  trigger,
  contentClassName,
  ...rest
}: DialogProps) {
  const isMobile = useIsMobile()
  // If open/onClose are provided, use controlled mode; else, uncontrolled
  const dialogProps =
    open !== undefined
      ? { open, onOpenChange: (v: boolean) => !v && onClose?.(), ...rest }
      : { ...rest }

  return (
    <UIDialog {...dialogProps}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className={`max-h-[90vh] overflow-hidden ${contentClassName ?? ''}`}>
        <DialogHeader className="shrink-0 flex items-center justify-between">
          {title ? (
            <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
          ) : (
            <VisuallyHidden>
              <DialogTitle>Dialog</DialogTitle>
            </VisuallyHidden>
          )}
        </DialogHeader>
        {description ? (
          <DialogDescription className="shrink-0">{description}</DialogDescription>
        ) : (
          <VisuallyHidden>
            <DialogDescription>Dialog content</DialogDescription>
          </VisuallyHidden>
        )}
        <div className={`flex-1 min-h-0 overflow-y-auto ${isMobile ? 'p-1' : 'p-4'}`}>{children}</div>
        {footer && (
          <DialogFooter className="shrink-0 flex justify-end gap-2 border-t p-3">
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </UIDialog>
  )
}
